/** Eight certificate-depth examples per part for Correlation & Regression. */

export const PS_REG_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Compute Pearson's r from raw data",
    setup: "Data: $(1,2),(2,4),(3,5),(4,8),(5,9)$. Find $r$.",
    steps: [
      { text: "$\\bar{x}=3$ and $\\bar{y}=\\dfrac{2+4+5+8+9}{5}=5.6$.", why: "Centre both variables." },
      { text: "$x$ deviations: $-2,-1,0,1,2$; $y$ deviations: $-3.6,-1.6,-0.6,2.4,3.4$.", why: "Building blocks of $r$." },
      { text: "Products: $7.2,1.6,0,2.4,6.8$ summing to $S_{xy}=18$.", why: "Covariation." },
      { text: "$S_{xx}=4+1+0+1+4=10$.", why: "Spread in $x$." },
      { text: "$S_{yy}=12.96+2.56+0.36+5.76+11.56=33.2$.", why: "Spread in $y$." },
      { text: "$r=\\dfrac{S_{xy}}{\\sqrt{S_{xx}S_{yy}}}=\\dfrac{18}{\\sqrt{332}}$.", why: "Definition." },
      { text: "$\\sqrt{332}\\approx18.221$, so $r\\approx0.988$.", why: "Compute." },
      { text: "Very strong positive linear association.", why: "Interpretation." },
    ],
    result: "$r\\approx0.988$",
    check: "$-1\\le r\\le1$ holds, and the scatter is nearly a straight rising line.",
    mistake: "Forgetting the square root in the denominator, which can push \"$r$\" outside $[-1,1]$.",
    life: "Engineers screen sensor pairs with $r$ before modelling: a correlation near $1$ means one sensor is nearly redundant.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Interpret r and r squared",
    setup: "A study reports $r=-0.6$ between hours of TV and exam score. Interpret $r$, $r^{2}$, and what changes if units change.",
    steps: [
      { text: "The sign is negative, so more TV goes with lower scores.", why: "Direction." },
      { text: "$|r|=0.6$ indicates a moderate linear relationship.", why: "Strength." },
      { text: "$r^{2}=0.36$.", why: "Square it." },
      { text: "So $36\\%$ of the variance in scores is explained by the linear fit on TV hours.", why: "Coefficient of determination." },
      { text: "The remaining $64\\%$ comes from everything else plus noise.", why: "Complement." },
      { text: "$r$ is unitless and unchanged if hours become minutes or scores become percentages.", why: "Standardized measure." },
      { text: "But the regression slope does change with units.", why: "Contrast with $r$." },
      { text: "Correlation does not establish causation: a study-time confounder could drive both.", why: "Essential caveat." },
    ],
    result: "Moderate negative association; $r^{2}=0.36$, so $36\\%$ of score variance is explained",
    check: "$r=-0.6$ and $r=+0.6$ describe equally strong relationships in opposite directions.",
    mistake: "Reading $r=-0.6$ as \"$60\\%$ of the variation explained\"; that figure is $r^{2}=36\\%$.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Correlation from summary statistics (with an audit)",
    setup: "A report gives $n=10$, $\\sum x=50$, $\\sum y=80$, $\\sum xy=460$, $\\sum x^{2}=300$, $\\sum y^{2}=700$. Compute $r$ and check the totals.",
    steps: [
      { text: "$S_{xy}=\\sum xy-\\dfrac{(\\sum x)(\\sum y)}{n}=460-\\dfrac{50(80)}{10}$.", why: "Computational formula." },
      { text: "$=460-400=60$.", why: "Compute." },
      { text: "$S_{xx}=300-\\dfrac{50^{2}}{10}=300-250=50$.", why: "Same pattern." },
      { text: "$S_{yy}=700-\\dfrac{80^{2}}{10}=700-640=60$.", why: "Third quantity." },
      { text: "$r=\\dfrac{60}{\\sqrt{50(60)}}=\\dfrac{60}{\\sqrt{3000}}$.", why: "Substitute." },
      { text: "$\\sqrt{3000}\\approx54.772$, giving $r\\approx1.095$, which is impossible.", why: "Correlation can never exceed $1$, so a reported total must be wrong." },
      { text: "Audit with Cauchy–Schwarz, $S_{xy}^{2}\\le S_{xx}S_{yy}$: here $3600>3000$, so the totals are inconsistent.", why: "This inequality is the fastest test of a summary table." },
      { text: "With a corrected $\\sum y^{2}=740$ we get $S_{yy}=100$ and $r=\\dfrac{60}{\\sqrt{5000}}\\approx0.849$.", why: "Consistent data give a legitimate $r$." },
    ],
    result: "The stated totals are impossible; with $\\sum y^{2}=740$ the correlation is $r\\approx0.849$",
    check: "Cauchy–Schwarz ($S_{xy}^{2}\\le S_{xx}S_{yy}$) is the fastest audit of any summary table.",
    mistake: "Reporting $|r|>1$ instead of recognizing an arithmetic error in the summaries.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Rank correlation with Spearman",
    setup: "Five students' ranks in maths and physics: $(1,2),(2,1),(3,4),(4,3),(5,5)$. Compute Spearman's $\\rho$.",
    steps: [
      { text: "Spearman's $\\rho$ is Pearson's $r$ applied to ranks.", why: "Definition." },
      { text: "Rank differences $d$: $-1,1,-1,1,0$.", why: "Subtract paired ranks." },
      { text: "Squares: $1,1,1,1,0$, so $\\sum d^{2}=4$.", why: "Needed for the shortcut." },
      { text: "Shortcut: $\\rho=1-\\dfrac{6\\sum d^{2}}{n(n^{2}-1)}$.", why: "Valid when there are no ties." },
      { text: "$n(n^{2}-1)=5(24)=120$.", why: "Denominator." },
      { text: "$\\rho=1-\\dfrac{6(4)}{120}=1-0.2=0.8$.", why: "Compute." },
      { text: "Strong positive agreement between the two rankings.", why: "Interpretation." },
      { text: "Spearman is robust to outliers and detects any monotone pattern, not just a straight line.", why: "When to prefer it." },
    ],
    result: "$\\rho=0.8$",
    check: "Perfect agreement would give $\\sum d^{2}=0$ and $\\rho=1$; complete reversal gives $\\rho=-1$.",
    mistake: "Using the raw scores in the shortcut formula instead of their ranks.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Outliers and influence on r",
    setup: "For $(1,1),(2,2),(3,3),(4,4)$ the correlation is $1$. Add the point $(10,1)$ and recompute.",
    steps: [
      { text: "Original data are perfectly collinear, so $r=1$.", why: "Baseline." },
      { text: "With the new point, $n=5$: $\\bar{x}=\\dfrac{20}{5}=4$ and $\\bar{y}=\\dfrac{11}{5}=2.2$.", why: "Recentre." },
      { text: "$x$ deviations: $-3,-2,-1,0,6$; $y$ deviations: $-1.2,-0.2,0.8,1.8,-1.2$.", why: "Recompute deviations." },
      { text: "Products: $3.6,0.4,-0.8,0,-7.2$, summing to $S_{xy}=-4$.", why: "The new point dominates." },
      { text: "$S_{xx}=9+4+1+0+36=50$.", why: "Spread in $x$ balloons." },
      { text: "$S_{yy}=1.44+0.04+0.64+3.24+1.44=6.8$.", why: "Spread in $y$." },
      { text: "$r=\\dfrac{-4}{\\sqrt{50(6.8)}}=\\dfrac{-4}{\\sqrt{340}}\\approx-0.217$.", why: "Compute." },
      { text: "One point flipped $r$ from $+1$ to $-0.22$ because of its extreme leverage in $x$.", why: "The lesson about influence." },
    ],
    result: "$r$ drops from $1$ to about $-0.217$",
    check: "The single new point contributes $-7.2$ of cross-product against $+4.0$ from all four original points.",
    mistake: "Trusting $r$ without plotting the data first.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Test whether a correlation is significant",
    setup: "$r=0.5$ from $n=20$ pairs. Test $H_0:\\rho=0$ at $\\alpha=0.05$.",
    steps: [
      { text: "Test statistic: $t=\\dfrac{r\\sqrt{n-2}}{\\sqrt{1-r^{2}}}$.", why: "Standard test for zero correlation." },
      { text: "$n-2=18$ degrees of freedom.", why: "Two parameters estimated." },
      { text: "$\\sqrt{18}\\approx4.243$.", why: "Numerator piece." },
      { text: "$1-r^{2}=0.75$, so $\\sqrt{0.75}\\approx0.866$.", why: "Denominator." },
      { text: "$t=\\dfrac{0.5(4.243)}{0.866}\\approx2.449$.", why: "Compute." },
      { text: "Two-tailed critical value $t_{0.025,18}=2.101$.", why: "Threshold." },
      { text: "$2.449>2.101$, so reject $H_0$: the correlation is significant.", why: "Decision." },
      { text: "But $r^{2}=0.25$, so only a quarter of the variance is explained — significant is not the same as strong.", why: "Effect size caveat." },
    ],
    result: "$t\\approx2.45$ with $18$ df — significant at $5\\%$ (p $\\approx0.025$)",
    check: "The critical $r$ for $n=20$ is about $0.444$, and $0.5$ exceeds it.",
    mistake: "Using $n$ instead of $n-2$ in the degrees of freedom.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Nonlinear relationship hidden from r",
    setup: "Data: $(-2,4),(-1,1),(0,0),(1,1),(2,4)$ follow $y=x^{2}$ exactly. Compute $r$ and explain.",
    steps: [
      { text: "$\\bar{x}=0$ and $\\bar{y}=\\dfrac{4+1+0+1+4}{5}=2$.", why: "Means." },
      { text: "$x$ deviations: $-2,-1,0,1,2$.", why: "Symmetric about zero." },
      { text: "$y$ deviations: $2,-1,-2,-1,2$.", why: "Subtract $\\bar{y}=2$." },
      { text: "Products: $-4,1,0,-1,4$.", why: "Pair them up." },
      { text: "$S_{xy}=-4+1+0-1+4=0$.", why: "Perfect cancellation from symmetry." },
      { text: "So $r=0$ despite a deterministic relationship.", why: "The headline result." },
      { text: "$r$ only measures *linear* association; the parabola has no linear trend here.", why: "Precise interpretation." },
      { text: "Regressing $y$ on $x^{2}$ instead gives $r=1$ — transform, then correlate.", why: "The practical fix." },
    ],
    result: "$r=0$ even though $y=x^{2}$ exactly",
    check: "A scatterplot shows a perfect U shape, which no straight line can capture.",
    mistake: "Concluding \"no relationship\" from $r=0$ without plotting.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Confounding and spurious correlation",
    setup: "Ice-cream sales correlate with drowning deaths at $r=0.8$. Analyze the causal claim and the role of temperature.",
    steps: [
      { text: "A high $r$ establishes association only.", why: "Correlation is symmetric; causation is not." },
      { text: "Candidate explanations: sales cause drownings, drownings cause sales, a common cause, or coincidence.", why: "Enumerate before concluding." },
      { text: "Temperature plausibly drives both: heat raises sales and swimming exposure.", why: "Confounder identified." },
      { text: "Conditioning on temperature (comparing days of equal heat) shrinks the association toward zero.", why: "Partial correlation logic." },
      { text: "Formally, $r_{xy\\cdot z}=\\dfrac{r_{xy}-r_{xz}r_{yz}}{\\sqrt{(1-r_{xz}^{2})(1-r_{yz}^{2})}}$.", why: "Partial correlation formula." },
      { text: "With $r_{xz}=r_{yz}=0.9$: numerator $=0.8-0.81=-0.01$.", why: "Substitute." },
      { text: "Denominator $=\\sqrt{(1-0.81)^{2}}=0.19$, so $r_{xy\\cdot z}\\approx-0.05$.", why: "Essentially zero." },
      { text: "Conclusion: the raw correlation is spurious, explained almost entirely by temperature.", why: "Answer to the causal question." },
    ],
    result: "Association is real but non-causal; controlling for temperature gives $r_{xy\\cdot z}\\approx-0.05$",
    check: "Banning ice cream would not reduce drownings — the test any causal claim must pass.",
    mistake: "Treating a strong correlation as evidence of causation without considering confounders.",
  },
];

export const PS_REG_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Least-squares line from scratch",
    setup: "Fit $\\hat{y}=a+bx$ to $(1,2),(2,4),(3,5),(4,8),(5,9)$.",
    steps: [
      { text: "$\\bar{x}=3$, $\\bar{y}=5.6$.", why: "The line passes through $(\\bar{x},\\bar{y})$." },
      { text: "$S_{xy}=18$ and $S_{xx}=10$ (from the Part 1 computation).", why: "Reuse the sums." },
      { text: "Slope $b=\\dfrac{S_{xy}}{S_{xx}}=\\dfrac{18}{10}=1.8$.", why: "Least-squares slope." },
      { text: "Intercept $a=\\bar{y}-b\\bar{x}=5.6-1.8(3)$.", why: "Force the line through the centroid." },
      { text: "$=5.6-5.4=0.2$.", why: "Compute." },
      { text: "So $\\hat{y}=0.2+1.8x$.", why: "Fitted line." },
      { text: "Interpretation: each unit of $x$ adds $1.8$ to predicted $y$.", why: "Slope in context." },
      { text: "At $x=3$: $\\hat{y}=5.6=\\bar{y}$, confirming the centroid property.", why: "Built-in check." },
    ],
    result: "$\\hat{y}=0.2+1.8x$",
    check: "$r^{2}=b^{2}S_{xx}/S_{yy}=3.24(10)/33.2\\approx0.976$, matching $r\\approx0.988$.",
    mistake: "Swapping the roles of $x$ and $y$; regressing $x$ on $y$ gives a different line.",
    life: "Utility companies fit exactly this line to predict load from temperature; the slope is the megawatts per degree they must have ready.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Predict and interpret carefully",
    setup: "Using $\\hat{y}=0.2+1.8x$ fitted on $1\\le x\\le5$, predict at $x=4$ and at $x=20$.",
    steps: [
      { text: "At $x=4$: $\\hat{y}=0.2+1.8(4)=7.4$.", why: "Substitute." },
      { text: "The observed value there was $8$, so the residual is $8-7.4=0.6$.", why: "Residual $=$ observed $-$ fitted." },
      { text: "This is interpolation: $x=4$ lies inside the fitted range.", why: "Reliable use of the model." },
      { text: "At $x=20$: $\\hat{y}=0.2+1.8(20)=36.2$.", why: "Arithmetic is easy; validity is not." },
      { text: "But $x=20$ is far outside $[1,5]$ — this is extrapolation.", why: "Key warning." },
      { text: "Nothing in the data supports linearity that far out.", why: "The relationship may bend or break." },
      { text: "Prediction intervals also widen with distance from $\\bar{x}$.", why: "Uncertainty grows quadratically in $(x-\\bar{x})$." },
      { text: "Report $36.2$ only with an explicit extrapolation caveat.", why: "Honest reporting." },
    ],
    result: "$\\hat{y}(4)=7.4$ (residual $0.6$); $\\hat{y}(20)=36.2$ but unreliable extrapolation",
    check: "Predictions at $x=1$ and $x=5$ give $2.0$ and $9.2$, close to the observed $2$ and $9$.",
    mistake: "Quoting extrapolated predictions with the same confidence as interpolated ones.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Residuals and the standard error of estimate",
    setup: "For the line $\\hat{y}=0.2+1.8x$ on the five data points, compute all residuals and $s_e$.",
    steps: [
      { text: "Fitted values: $2.0,3.8,5.6,7.4,9.2$.", why: "Evaluate the line at each $x$." },
      { text: "Residuals $e=y-\\hat{y}$: $0,\\;0.2,\\;-0.6,\\;0.6,\\;-0.2$.", why: "Subtract." },
      { text: "They sum to zero — always true for a least-squares fit with an intercept.", why: "Arithmetic check." },
      { text: "Squared residuals: $0,\\;0.04,\\;0.36,\\;0.36,\\;0.04$.", why: "Toward SSE." },
      { text: "$\\mathrm{SSE}=0.80$.", why: "Sum." },
      { text: "$s_e=\\sqrt{\\dfrac{\\mathrm{SSE}}{n-2}}=\\sqrt{\\dfrac{0.8}{3}}$.", why: "Two parameters estimated." },
      { text: "$\\approx\\sqrt{0.2667}\\approx0.516$.", why: "Typical prediction error in $y$ units." },
      { text: "Also $\\sum e_ix_i=0$, the second normal equation.", why: "Residuals are orthogonal to the predictor." },
    ],
    result: "Residuals $0,0.2,-0.6,0.6,-0.2$ with $\\mathrm{SSE}=0.8$ and $s_e\\approx0.52$",
    check: "$\\mathrm{SST}=S_{yy}=33.2$ and $\\mathrm{SSR}=32.4$, so $\\mathrm{SSR}+\\mathrm{SSE}=33.2$.",
    mistake: "Dividing SSE by $n$ instead of $n-2$ when estimating $s_e$.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Decompose variance and read R squared",
    setup: "With $\\mathrm{SST}=33.2$ and $\\mathrm{SSE}=0.8$, find $R^{2}$ and explain what it does and does not say.",
    steps: [
      { text: "$\\mathrm{SSR}=\\mathrm{SST}-\\mathrm{SSE}=33.2-0.8=32.4$.", why: "Variance decomposition." },
      { text: "$R^{2}=\\dfrac{\\mathrm{SSR}}{\\mathrm{SST}}=\\dfrac{32.4}{33.2}$.", why: "Definition." },
      { text: "$\\approx0.976$.", why: "Compute." },
      { text: "So $97.6\\%$ of the variation in $y$ is explained by the linear model.", why: "Interpretation." },
      { text: "For simple regression, $R^{2}=r^{2}$: $0.988^{2}\\approx0.976$.", why: "Consistency check." },
      { text: "A high $R^{2}$ does not prove linearity — check residual plots.", why: "Anscombe's quartet warning." },
      { text: "It also does not establish causation or good extrapolation.", why: "Limits of the statistic." },
      { text: "Adding predictors never lowers $R^{2}$, which is why adjusted $R^{2}$ exists.", why: "Multiple-regression caveat." },
    ],
    result: "$R^{2}\\approx0.976$ — the model explains about $97.6\\%$ of the variance in $y$",
    check: "$\\mathrm{SSE}/\\mathrm{SST}=0.024$, and $0.976+0.024=1$.",
    mistake: "Reporting $R^{2}$ as a percentage of data points fitted rather than variance explained.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Confidence interval for the slope",
    setup: "With $b=1.8$, $s_e=0.516$, $S_{xx}=10$, $n=5$, build a $95\\%$ CI for the true slope and test $H_0:\\beta=0$.",
    steps: [
      { text: "Standard error of the slope: $\\mathrm{SE}(b)=\\dfrac{s_e}{\\sqrt{S_{xx}}}$.", why: "More spread in $x$ means a better-determined slope." },
      { text: "$=\\dfrac{0.516}{\\sqrt{10}}\\approx0.1633$.", why: "Compute." },
      { text: "Degrees of freedom $=n-2=3$; $t_{0.025,3}=3.182$.", why: "Small-sample critical value." },
      { text: "Margin $=3.182(0.1633)\\approx0.520$.", why: "Half-width." },
      { text: "CI: $1.8\\pm0.52=[1.28,\\,2.32]$.", why: "Interval estimate." },
      { text: "The interval excludes $0$, so the slope is significant.", why: "Duality with the test." },
      { text: "Direct test: $t=\\dfrac{1.8}{0.1633}\\approx11.0$, far beyond $3.182$.", why: "Same conclusion." },
      { text: "p-value $\\approx0.0016$.", why: "Strong evidence of a real linear effect." },
    ],
    result: "$95\\%$ CI for the slope $[1.28,2.32]$; $t\\approx11.0$, so the slope differs from zero",
    check: "Testing the slope is equivalent to testing $\\rho=0$ in simple regression, and both reject.",
    mistake: "Using $n-1$ degrees of freedom, which understates the uncertainty.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Residual plot diagnoses a wrong model",
    setup: "A line is fitted to data generated by $y=x^{2}$ at $x=1,\\dots,5$. Analyze the residual pattern.",
    steps: [
      { text: "Data: $(1,1),(2,4),(3,9),(4,16),(5,25)$; $\\bar{x}=3$, $\\bar{y}=11$.", why: "Setup." },
      { text: "$S_{xy}=(-2)(-10)+(-1)(-7)+0(-2)+1(5)+2(14)=20+7+0+5+28=60$.", why: "Cross-products." },
      { text: "$S_{xx}=10$, so $b=6$ and $a=11-18=-7$.", why: "Least-squares fit." },
      { text: "Fitted values: $-1,5,11,17,23$.", why: "Evaluate $\\hat{y}=-7+6x$." },
      { text: "Residuals: $2,-1,-2,-1,2$.", why: "Observed minus fitted." },
      { text: "The residuals form a clear U shape rather than random scatter.", why: "The diagnostic signal." },
      { text: "Curvature in residuals means the linear form is wrong, even though $R^{2}\\approx0.959$ looks impressive.", why: "High $R^{2}$ can hide misspecification." },
      { text: "Refit with $x^{2}$ as the predictor: residuals vanish and $R^{2}=1$.", why: "The remedy." },
    ],
    result: "Residuals $2,-1,-2,-1,2$ show systematic curvature — the linear model is misspecified despite $R^{2}\\approx0.96$",
    check: "Residuals still sum to zero, so the fit is valid least squares; only the model form is wrong.",
    mistake: "Accepting a model on $R^{2}$ alone without inspecting residuals.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Linearize an exponential model",
    setup: "Bacteria counts: $(0,100),(1,200),(2,400),(3,800)$. Fit $y=Ae^{kt}$.",
    steps: [
      { text: "Take logs: $\\ln y=\\ln A+kt$, which is linear in $t$.", why: "Transform to use least squares." },
      { text: "$\\ln y$ values: $4.605,5.298,5.991,6.685$.", why: "Natural logs of the counts." },
      { text: "Differences are constant at about $0.693=\\ln2$.", why: "Perfect exponential signature." },
      { text: "$\\bar{t}=1.5$ and $\\overline{\\ln y}=5.645$.", why: "Centres." },
      { text: "$S_{t,\\ln y}=(-1.5)(-1.040)+(-0.5)(-0.347)+0.5(0.346)+1.5(1.040)\\approx3.466$.", why: "Cross-products." },
      { text: "$S_{tt}=2.25+0.25+0.25+2.25=5$, so $k=3.466/5\\approx0.693$.", why: "Slope in log space." },
      { text: "$\\ln A=5.645-0.693(1.5)=4.605$, so $A=e^{4.605}=100$.", why: "Intercept back-transformed." },
      { text: "Model: $y=100e^{0.693t}=100\\cdot2^{t}$, doubling every unit of time.", why: "Interpretable final form." },
    ],
    result: "$y=100e^{0.693t}$, i.e. exactly a doubling per time unit",
    check: "At $t=3$: $100(2^{3})=800$, matching the data exactly.",
    mistake: "Fitting a straight line to the raw counts, which underpredicts early and late values.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Leverage, influence, and refitting",
    setup: "To $(1,2),(2,4),(3,6),(4,8)$ (a perfect line $y=2x$) add $(10,5)$. Refit and quantify the damage.",
    steps: [
      { text: "Original fit: $b=2$, $a=0$, $R^{2}=1$.", why: "Baseline." },
      { text: "With the new point, $n=5$: $\\bar{x}=4$, $\\bar{y}=5$.", why: "Recentre." },
      { text: "$x$ deviations: $-3,-2,-1,0,6$; $y$ deviations: $-3,-1,1,3,0$.", why: "Recompute." },
      { text: "$S_{xy}=9+2-1+0+0=10$.", why: "Cross-products." },
      { text: "$S_{xx}=9+4+1+0+36=50$, so $b=0.2$.", why: "The slope collapses from $2$ to $0.2$." },
      { text: "$a=5-0.2(4)=4.2$, giving $\\hat{y}=4.2+0.2x$.", why: "New line." },
      { text: "Leverage of the new point: $h=\\dfrac1n+\\dfrac{(x-\\bar{x})^{2}}{S_{xx}}=0.2+\\dfrac{36}{50}=0.92$.", why: "Very high; the average leverage is $2/5=0.4$." },
      { text: "Since $h=0.92$ is more than twice the average, the point is both high-leverage and influential — investigate or report both fits.", why: "Standard diagnostic practice." },
    ],
    result: "Slope falls from $2$ to $0.2$; the added point has leverage $h=0.92$",
    check: "Removing the point restores $\\hat{y}=2x$ exactly, confirming its influence.",
    mistake: "Deleting influential points silently instead of reporting the sensitivity.",
  },
];
